import { useState, useEffect, Suspense } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { IDE } from './IDE/IDE';
import { PlanModeDashboard } from './PlanMode/PlanModeDashboard';
import { ViewSkeleton } from './Common/SuspenseFallback';
import { AppLayout } from './Layout/AppLayout';
import { ViewRouter } from './ViewRouter';
import { logger } from '../services/loggingService';
import type { NavSection } from '../types';

export function MainApp() {
  const { user } = useAuth();
  const { projectId: routeProjectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [currentView, setCurrentView] = useState<'dashboard' | 'ide' | 'plan'>('dashboard');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<NavSection>('welcome');

  // Handle route changes using React Router
  useEffect(() => {
    if (!user) return;

    const pathname = location.pathname;
      
    // Handle IDE and Plan Mode routes (project-specific views)
    if (pathname.startsWith('/ide/')) {
      if (routeProjectId) {
        setSelectedProjectId(routeProjectId);
        setCurrentView('ide');
        return;
      }
    }
    
    // Handle /ide without projectId - show IDE with project selector
    if (pathname === '/ide') {
      setActiveSection('ide');
      setCurrentView('dashboard');
      setSelectedProjectId(null);
      return;
    }
    
    if (pathname.startsWith('/plan/')) {
      if (routeProjectId) {
        setSelectedProjectId(routeProjectId);
        setCurrentView('plan');
        return;
      }
    }
    
    // Handle /plan without projectId - show plan view
    if (pathname === '/plan') {
      setActiveSection('plan');
      setCurrentView('dashboard');
      setSelectedProjectId(null);
      return;
    }

    // Handle dashboard section routes
    const sectionMap: Record<string, NavSection> = {
      '/dashboard': 'welcome',
      '/agents': 'agents',
      '/blueprint': 'blueprint',
      '/ide': 'ide', // This will show IDE selector in ViewRouter
      '/jira': 'jira',
      '/tickets': 'jira',
      '/plan': 'plan',
      '/build': 'build',
      '/execution': 'execution',
      '/code-review': 'review',
      '/review': 'review',
      '/reports': 'reports',
      '/usage': 'usage',
      '/pricing': 'pricing',
      '/billing': 'billing',
      '/integrations': 'integrations',
      '/organization': 'organization',
      '/users': 'users',
      '/admin': 'admin',
    };
        
    if (sectionMap[pathname]) {
      setActiveSection(sectionMap[pathname]);
      setCurrentView('dashboard');
      setSelectedProjectId(null);
      return;
    }

    // Default to dashboard
    setActiveSection('welcome');
    setCurrentView('dashboard');
    setSelectedProjectId(null);
  }, [location, routeProjectId, user, navigate]);

  const handleSelectProject = (projectId: string) => {
    setSelectedProjectId(projectId);
    // Navigate to IDE view for the selected project using React Router
    navigate(`/ide/${projectId}`);
    setCurrentView('ide');
  };

  const handleSectionChange = (section: NavSection) => {
    setActiveSection(section);
    
    // Handle special sections that require project navigation
    if (section === 'ide' && selectedProjectId) {
      navigate(`/ide/${selectedProjectId}`);
      setCurrentView('ide');
      return;
    }
    
    if (section === 'blueprint' && selectedProjectId) {
      navigate(`/plan/${selectedProjectId}`);
      setCurrentView('plan');
      return;
    }
    
    // Map section to URL path
    const sectionToPath: Partial<Record<NavSection, string>> = {
      'welcome': '/dashboard',
      'blueprint': '/blueprint',
      'build': '/build',
      'review': '/code-review',
      'audit': '/audit',
      'jira': '/jira',
      'reports': '/reports',
      'standards': '/standards',
      'prompts': '/prompts',
      'ide': '/ide',
      'agents': '/agents',
      'integrations': '/integrations',
      'usage': '/usage',
      'pricing': '/pricing',
      'billing': '/billing',
      'admin': '/admin',
      'users': '/users',
      'architecture': '/architecture',
      'organization': '/organization',
      'error-logs': '/error-logs',
    };
    
    const path = sectionToPath[section] || '/dashboard';
    navigate(path);
    setCurrentView('dashboard');
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;

      // If deleted project was selected, go back to dashboard
      if (selectedProjectId === projectId) {
        setSelectedProjectId(null);
        setCurrentView('dashboard');
        navigate('/dashboard');
      }
    } catch (error) {
      logger.error('Failed to delete project', error);
    }
  };

  const handleProjectCreated = (projectId: string) => {
    setSelectedProjectId(projectId);
    // Optionally navigate to the new project
    navigate(`/ide/${projectId}`);
    setCurrentView('ide');
  };

  if (!user) {
    return null;
  }

  // IDE and PlanModeDashboard have their own layouts, so don't wrap them
  // IDE can be shown with or without projectId (will show project selector if missing)
  if (currentView === 'ide') {
    return (
      <Suspense fallback={<ViewSkeleton />}>
        <IDE projectId={selectedProjectId || undefined} />
      </Suspense>
    );
  }

  if (currentView === 'plan' && selectedProjectId) {
    return (
      <Suspense fallback={<ViewSkeleton />}>
        <PlanModeDashboard 
          projectId={selectedProjectId} 
          userId={user.id}
        />
      </Suspense>
    );
  }

  // Dashboard views need AppLayout wrapper for Header and Sidebar
  return (
    <AppLayout
      activeSection={activeSection}
      onSectionChange={handleSectionChange}
      projectId={selectedProjectId}
    >
      <ViewRouter
        activeSection={activeSection}
        projectId={selectedProjectId}
        onSelectProject={handleSelectProject}
        onDeleteProject={handleDeleteProject}
        onProjectCreated={handleProjectCreated}
      />
    </AppLayout>
  );
}

